import React from 'react';
import { Switch } from '@headlessui/react';
import { cn } from '@/utils/cn';
import type { ToggleSwitchProps } from './ToggleSwitch.types';

/**
 * # ToggleSwitch
 *
 * Prepinac stavu (ON/OFF).
 *
 * @example
 * <ToggleSwitch checked={enabled} onChange={setEnabled} label="Povolit notifikace" />
 */
export const ToggleSwitch: React.FC<ToggleSwitchProps> = ({
  checked,
  onChange,
  label,
  description,
  size = 'md',
  disabled,
  className,
  'data-testid': testId,
}) => {
  const switchSizeClasses = {
    sm: 'h-5 w-9',
    md: 'h-6 w-11',
    lg: 'h-7 w-14',
  }[size];

  const translateCheckedClasses = {
    sm: 'translate-x-3.5',
    md: 'translate-x-5',
    lg: 'translate-x-7',
  }[size];

  return (
    <Switch.Group as="div" className={cn('flex items-start gap-3', className)}>
      <div className="flex items-center pt-0.5">
        <Switch
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          data-testid={testId}
          className={cn(
            checked ? 'bg-primary-600 dark:bg-primary-500' : 'bg-gray-200 dark:bg-zinc-700',
            disabled && 'opacity-50 cursor-not-allowed',
            'relative inline-flex flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:ring-offset-2 dark:focus:ring-offset-zinc-900',
            switchSizeClasses,
          )}
        >
          <span className="sr-only">{typeof label === 'string' ? label : 'Toggle'}</span>
          <span
            aria-hidden="true"
            className={cn(
              checked ? translateCheckedClasses : 'translate-x-0',
              'pointer-events-none inline-block transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
              size === 'sm' ? 'h-4 w-4' : size === 'md' ? 'h-5 w-5' : 'h-6 w-6',
              // Correction: thumb size must fit inside switch.
              // sm: h-5 (20px). border-2 -> 16px content. thumb h-4 is perfect.
              // md: h-6 (24px). border-2 -> 20px content. thumb h-5 is perfect.
              // lg: h-7 (28px). border-2 -> 24px content. thumb h-6 is perfect.
            )}
          />
        </Switch>
      </div>

      {(label || description) && (
        <div className="flex flex-col">
          {label && (
            <Switch.Label
              className={cn(
                'text-sm font-medium text-gray-900 dark:text-zinc-100 cursor-pointer select-none',
                disabled && 'cursor-not-allowed opacity-50',
              )}
            >
              {label}
            </Switch.Label>
          )}
          {description && (
            <Switch.Description className="text-sm text-gray-500 dark:text-zinc-400">
              {description}
            </Switch.Description>
          )}
        </div>
      )}
    </Switch.Group>
  );
};
